import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Home, MapPin, CheckCircle2, AlertCircle, Loader2, LogIn, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import Header from "../components/Header";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ClaimWithToken = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const [listing, setListing] = useState(null);
  const [phoneHint, setPhoneHint] = useState(null);
  const [error, setError] = useState(null);
  const [claimed, setClaimed] = useState(false);

  useEffect(() => {
    fetchClaimInfo();
  }, [token]);

  const fetchClaimInfo = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(`${API}/listings/claim/${token}`);
      setListing(response.data.listing);
      setPhoneHint(response.data.phone_hint);
    } catch (error) {
      console.error("Error fetching claim info:", error);
      setError(error.response?.data?.detail || "Link invalid sau expirat");
    } finally {
      setLoading(false);
    }
  };

  const handleClaim = async () => {
    if (!isAuthenticated) {
      toast.error("Trebuie să te autentifici pentru a revendica anunțul");
      navigate(`/auth?redirect=/claim/${token}`);
      return;
    }

    setClaiming(true);
    
    try {
      const response = await axios.post(`${API}/listings/claim/${token}`);
      setClaimed(true);
      toast.success("Anunț revendicat cu succes!");
      
      // Redirect to dashboard after 2 seconds
      setTimeout(() => {
        navigate("/dashboard");
      }, 2000);
    } catch (error) {
      console.error("Error claiming listing:", error);
      toast.error(error.response?.data?.detail || "Eroare la revendicarea anunțului");
    } finally {
      setClaiming(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-100">
        <Header />
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-stone-100">
        <Header />
        <div className="max-w-lg mx-auto px-4 py-16">
          <div className="bg-white rounded-2xl p-8 card-shadow text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-stone-900 mb-2">Link invalid</h1>
            <p className="text-stone-500 mb-6">{error}</p>
            <Link to="/">
              <Button className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full">
                <Home className="w-4 h-4 mr-2" />
                Înapoi la pagina principală
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (claimed) {
    return (
      <div className="min-h-screen bg-stone-100">
        <Header />
        <div className="max-w-lg mx-auto px-4 py-16">
          <div className="bg-white rounded-2xl p-8 card-shadow text-center">
            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-emerald-600" />
            </div>
            <h1 className="text-2xl font-bold text-stone-900 mb-2">Anunț revendicat!</h1>
            <p className="text-stone-500 mb-6">
              Anunțul "{listing?.title}" a fost adăugat în contul tău.
              Poți să îl editezi și să adaugi mai multe detalii.
            </p>
            <Link to="/dashboard">
              <Button className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full">
                Mergi la Dashboard
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-100">
      <Header />
      
      <main className="max-w-2xl mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl card-shadow overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-emerald-500 to-teal-600 p-6 text-white">
            <h1 className="text-2xl font-bold mb-2">Revendică acest anunț</h1>
            <p className="text-emerald-100">
              Acest anunț a fost detectat automat. Dacă ești proprietarul, revendică-l pentru a-l gestiona.
            </p>
          </div>

          {/* Listing Preview */}
          {listing && (
            <div className="p-6 border-b border-stone-100">
              <div className="flex gap-4">
                <div className="w-24 h-24 rounded-lg overflow-hidden flex-shrink-0 bg-stone-100">
                  <img
                    src={listing.images?.[0] || "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=200"}
                    alt={listing.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <Badge className="bg-amber-100 text-amber-700 mb-2">
                    {listing.status === 'detected' ? 'Detectat' : listing.status}
                  </Badge>
                  <h2 className="font-semibold text-stone-900 text-lg">{listing.title}</h2>
                  <p className="text-stone-500 flex items-center gap-1 mt-1">
                    <MapPin className="w-4 h-4" />
                    {listing.zone || listing.city}
                  </p>
                  <p className="text-emerald-700 font-bold text-xl mt-2">
                    {listing.price?.toLocaleString('ro-RO')} {listing.currency || '€'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Action Area */}
          <div className="p-6">
            {phoneHint && (
              <div className="bg-stone-50 rounded-lg p-4 mb-6">
                <p className="text-sm text-stone-600">
                  Anunțul original conține un număr de telefon terminat în <strong>****{phoneHint}</strong>
                </p>
              </div>
            )}

            {isAuthenticated ? (
              <div className="space-y-4">
                <div className="bg-emerald-50 rounded-lg p-4">
                  <p className="text-emerald-800">
                    <strong>Conectat ca:</strong> {user?.name || user?.email}
                  </p>
                  <p className="text-sm text-emerald-600 mt-1">
                    Anunțul va fi adăugat în contul tău și vei putea să îl editezi.
                  </p>
                </div>
                
                <Button
                  onClick={handleClaim}
                  disabled={claiming}
                  className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl"
                  data-testid="claim-btn"
                >
                  {claiming ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Se revendică...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-5 h-5 mr-2" />
                      Revendică anunțul
                    </>
                  )}
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-amber-50 rounded-lg p-4">
                  <p className="text-amber-800">
                    <strong>Nu ești autentificat</strong>
                  </p>
                  <p className="text-sm text-amber-600 mt-1">
                    Trebuie să te autentifici sau să îți creezi un cont pentru a revendica acest anunț.
                  </p>
                </div>
                
                <Link to={`/auth`} state={{ from: { pathname: `/claim/${token}` } }}>
                  <Button className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl">
                    <LogIn className="w-5 h-5 mr-2" />
                    Autentifică-te pentru a revendica
                  </Button>
                </Link>
              </div>
            )}
          </div>

          {/* Info */}
          <div className="bg-stone-50 px-6 py-4 border-t border-stone-100">
            <h3 className="font-medium text-stone-700 mb-2">Ce poți face după revendicare:</h3>
            <ul className="text-sm text-stone-500 space-y-1">
              <li>• Editezi titlul și descrierea</li>
              <li>• Adaugi fotografii noi</li>
              <li>• Generezi descriere cu AI</li>
              <li>• Vezi cererile de vizionare</li>
              <li>• Folosești simulatorul de credit</li>
            </ul>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ClaimWithToken;
